import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DATE_DISPLAY_FORMAT, toApiDate } from "../../utils/dateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import { CheckCircle, ContentCopy, Pin, QrCode2, ArrowBack, Fullscreen, FullscreenExit, Close, ChevronLeft, ChevronRight, Download, Visibility } from "@mui/icons-material";
import QRCode from "react-qr-code";
import attendanceService from "../../services/attendanceService";
import { getTeacherClasses } from "../../services/teacherService";
import classSubjectService from "../../services/classSubjectService";
import scheduleService from "../../services/scheduleService";
import specialSessionService from "../../services/specialSessionService";
import { getAppSocket } from "../../hooks/useSocket";
import {
  formatTimeRange12h,
  formatDateIST,
  formatDateTimeIST,
  getISTTimeParts,
  timeValueToSecondsOfDay,
  isSlotInLiveLectureWindowIST,
} from "../../utils/timetableTimeSlots";
import { formatLectureScheduleStatus } from "../../utils/lectureScheduleStatus";
import { usePermissions } from "../../hooks/usePermissions";
import { useDepartments } from "../../hooks/queries/useDepartments";
import { useClasses } from "../../hooks/queries/useCourses";
import LiveRosterDialog from "./LiveRosterDialog";

const toDateString = (value) => formatDateIST(value ? new Date(value) : new Date());

const parseTimestampMs = (value) => {
  const ms = value != null ? new Date(value).getTime() : Number.NaN;
  return Number.isFinite(ms) ? ms : null;
};

const toIsoTimestamp = (value) => {
  const ms = parseTimestampMs(value);
  return ms != null ? new Date(ms).toISOString() : null;
};

const normalizeAttendancePair = (
  payload,
  {
    requireLectureContext = false,
    requestStartedAtMs,
    responseReceivedAtMs,
    fallbackOffsetMs,
  } = {}
) => {
  const data = payload?.data?.data ?? payload?.data ?? payload ?? {};
  if (data?.ok === false) return null;

  const sessionId = data.sessionId != null ? String(data.sessionId) : "";
  const publicId = data.publicId != null ? String(data.publicId) : "";
  const otp = data.otp != null ? String(data.otp) : "";
  const qrTokenId = data.qrTokenId != null ? String(data.qrTokenId) : "";
  const otpTokenId = data.otpTokenId != null ? String(data.otpTokenId) : "";
  const tokenIssuedAt = toIsoTimestamp(data.tokenIssuedAt);
  const tokenValidUntil = toIsoTimestamp(data.tokenValidUntil);
  const expiryTime = toIsoTimestamp(data.expiryTime);
  const serverTime = toIsoTimestamp(data.serverTime);
  const lectureDate = data.lectureDate ? String(data.lectureDate).slice(0, 10) : "";
  const timeTableSlotId = data.timeTableSlotId != null ? String(data.timeTableSlotId) : "";
  // Patent claims 1.5 / 6.4 / 4.1 / 9: generating-device coordinates + identity to embed in
  // the QR. These are optional (legacy sessions omit them) so they are NOT part of the
  // required-field gate below — a missing value simply means the QR carries no coord/identity.
  const creatorLat = Number.isFinite(Number(data.creatorLat)) && data.creatorLat != null ? Number(data.creatorLat) : null;
  const creatorLng = Number.isFinite(Number(data.creatorLng)) && data.creatorLng != null ? Number(data.creatorLng) : null;
  const generatedBy = Number.isFinite(Number(data.generatedBy)) && data.generatedBy != null ? Number(data.generatedBy) : null;
  const serverTimeMs = parseTimestampMs(serverTime);
  const startedAtMs = Number(requestStartedAtMs);
  const receivedAtMs = Number(responseReceivedAtMs);
  const hasMeasuredClock = Number.isFinite(startedAtMs) &&
    Number.isFinite(receivedAtMs) && receivedAtMs >= startedAtMs;
  const clockSampleRttMs = hasMeasuredClock ? receivedAtMs - startedAtMs : null;
  const requestMidpointMs = hasMeasuredClock ? (startedAtMs + receivedAtMs) / 2 : null;
  const fallback = Number(fallbackOffsetMs);
  const serverTimeOffsetMs = serverTimeMs == null
    ? 0
    : requestMidpointMs != null
      ? serverTimeMs - requestMidpointMs
      : Number.isFinite(fallback)
        ? fallback
        : 0;

  if (
    !sessionId ||
    !publicId ||
    !otp ||
    !qrTokenId ||
    !otpTokenId ||
    !tokenIssuedAt ||
    !tokenValidUntil ||
    !expiryTime ||
    serverTimeMs == null ||
    (requireLectureContext && (!lectureDate || !timeTableSlotId))
  ) {
    return null;
  }

  return {
    sessionId,
    publicId,
    otp,
    qrTokenId,
    otpTokenId,
    tokenIssuedAt,
    tokenValidUntil,
    expiryTime,
    serverTime,
    serverTimeOffsetMs,
    clockSampleRttMs,
    lectureDate,
    timeTableSlotId,
    sessionTime: data.sessionTime || null,
    // Claim 1.5 / 6.4 / 4.1 / 9 — carried through so the QR render can embed them.
    creatorLat,
    creatorLng,
    generatedBy,
  };
};

// Patent claims 1.5 / 6.4 / 4.1 / 9: build the value encoded into the displayed QR so that the
// generated time-sensitive QR comprises the generating device's current latitude/longitude and
// the identity of the generating user "embedded therein" (plus the issue timestamp). Fields that
// are absent (legacy sessions) are omitted; a scanner that only understands the bare publicId can
// still read it from `publicId`. Never throws — falls back to the bare publicId string.
const buildQrCodeValue = (session) => {
  const publicId = session?.publicId ? String(session.publicId) : "";
  if (!publicId) return "";
  try {
    const payload = { publicId };
    if (session.sessionId != null) payload.sessionId = session.sessionId;
    if (session.creatorLat != null) payload.lat = Number(session.creatorLat);
    if (session.creatorLng != null) payload.lng = Number(session.creatorLng);
    if (session.tokenIssuedAt) payload.ts = session.tokenIssuedAt;
    if (session.generatedBy != null) payload.gen = Number(session.generatedBy);
    return JSON.stringify(payload);
  } catch (_e) {
    return publicId;
  }
};

const isAttendancePairFresh = (pair) => {
  if (!pair) return false;
  const serverNowMs = Date.now() + Number(pair.serverTimeOffsetMs || 0);
  return (
    parseTimestampMs(pair.tokenValidUntil) > serverNowMs &&
    parseTimestampMs(pair.expiryTime) > serverNowMs
  );
};

const attendancePairsMatch = (left, right) => {
  if (!left || !right) return false;
  return [
    "sessionId",
    "publicId",
    "otp",
    "qrTokenId",
    "otpTokenId",
    "tokenIssuedAt",
    "tokenValidUntil",
    "expiryTime",
    "lectureDate",
    "timeTableSlotId",
  ].every((field) => String(left[field] ?? "") === String(right[field] ?? ""));
};

const compareTokenId = (left, right) => {
  if (!/^\d+$/.test(String(left)) || !/^\d+$/.test(String(right))) return 0;
  const leftId = String(left).replace(/^0+(?=\d)/, "");
  const rightId = String(right).replace(/^0+(?=\d)/, "");
  if (leftId.length !== rightId.length) return leftId.length > rightId.length ? 1 : -1;
  return leftId === rightId ? 0 : leftId > rightId ? 1 : -1;
};

const compareAttendancePairGeneration = (incoming, current) => {
  if (!current) return "newer";
  if (String(incoming.sessionId) !== String(current.sessionId)) return "stale";
  if (
    incoming.qrTokenId === current.qrTokenId &&
    incoming.otpTokenId === current.otpTokenId &&
    incoming.publicId === current.publicId &&
    incoming.otp === current.otp
  ) {
    return "duplicate";
  }
  const incomingIssuedAt = parseTimestampMs(incoming.tokenIssuedAt);
  const currentIssuedAt = parseTimestampMs(current.tokenIssuedAt);
  if (incomingIssuedAt !== currentIssuedAt) {
    return incomingIssuedAt > currentIssuedAt ? "newer" : "stale";
  }
  const incomingValidUntil = parseTimestampMs(incoming.tokenValidUntil);
  const currentValidUntil = parseTimestampMs(current.tokenValidUntil);
  if (incomingValidUntil !== currentValidUntil) {
    return incomingValidUntil > currentValidUntil ? "newer" : "stale";
  }
  return compareTokenId(incoming.qrTokenId, current.qrTokenId) > 0 &&
    compareTokenId(incoming.otpTokenId, current.otpTokenId) > 0
    ? "newer"
    : "stale";
};

const requestWithClockSample = async (request) => {
  const requestStartedAtMs = Date.now();
  const response = await request();
  return { response, requestStartedAtMs, responseReceivedAtMs: Date.now() };
};

const isTerminalRotationFailure = (value) => {
  const code = value?.code || value?.response?.data?.code;
  const status = Number(value?.status || value?.response?.status || value?.response?.data?.status);
  return code === "SESSION_EXPIRED" || code === "SESSION_NOT_FOUND" || status === 404 || status === 410;
};

const normalizeClasses = (payload) => {
  const data = payload?.data ?? payload;
  const list =
    data?.data?.classData ||
    data?.classData ||
    data?.data?.rows ||
    data?.rows ||
    data?.data ||
    data ||  // Handle direct array response from teacher endpoint
    [];
  return Array.isArray(list)
    ? list.map((c) => ({
        id: c.id ?? c.classId,
        name: c.name || c.className || `Class ${c.id ?? c.classId}`,
        section: c.section ?? c.classSection ?? c.sectionName ?? "",
      }))
    : [];
};

const formatClassLabel = (cls) => {
  const name = cls?.name || cls?.className || "";
  const section = cls?.section ?? cls?.classSection ?? cls?.sectionName ?? "";
  return section ? `${name} - ${section}` : name || "—";
};

const normalizeSubjects = (payload) => {
  const data = payload?.data ?? payload;
  const list = data?.data?.subjects || data?.subjects || data?.data || [];
  return Array.isArray(list)
    ? list.map((s) => ({ id: s.id || s.subjectId, name: s.name || s.subjectName || `Subject ${s.id}` }))
    : [];
};

/**
 * Normalize the RAW axios response returned by `useDepartments` (pass-through hook).
 * Shape can be response.data.data (array), response.data (array), or response.data.departments.
 * Always returns a guarded array (no-crash rule 5).
 */
const normalizeDepartmentsResponse = (payload) => {
  const rows =
    payload?.data?.data ??
    payload?.data?.departments ??
    payload?.data ??
    [];
  const list = Array.isArray(rows) ? rows : Array.isArray(rows?.departments) ? rows.departments : [];
  return list.map((d) => ({
    id: d?.id ?? d?.departmentId ?? d?._id,
    name: d?.name || d?.departmentName || `Department ${d?.id ?? d?.departmentId ?? ""}`,
  }));
};

/**
 * Normalize the RAW axios response returned by `useClasses` (admin-scoped class list).
 * Distinct from the teacher endpoint shape handled by `normalizeClasses`.
 */
const normalizeAdminClassesResponse = (payload) => {
  const rows =
    payload?.data?.data?.classData ??
    payload?.data?.classData ??
    payload?.data?.classes ??
    payload?.data?.data ??
    payload?.data ??
    [];
  const list = Array.isArray(rows) ? rows : [];
  return list.map((c) => ({
    id: c?.id ?? c?.classId,
    name: c?.name || c?.className || `Class ${c?.id ?? c?.classId}`,
    section: c?.section ?? c?.classSection ?? c?.sectionName ?? "",
  }));
};

const escapeCsvCell = (v) => {
  const s = String(v ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/** Display label for Method column (backend sends "On Leave" for approved-leave present rows). */
const formatAttendanceMethodDisplay = (row) => {
  const method = String(row?.method ?? "").trim();
  if (!method) return null;
  if (method === "On Leave") return "On Leave";
  if (method.toUpperCase() === "MANUAL") return "Manual";
  if (method.toUpperCase() === "QR") return "QR";
  if (method.toUpperCase() === "OTP") return "OTP";
  return method;
};

const downloadScheduleAttendanceCsv = (schedule) => {
  const topic = schedule?.lectureTopic ?? "";
  const isLab = String(schedule?.lectureType || schedule?.sessionKind || "").toLowerCase().includes("lab") ||
    String(schedule?.lectureType || schedule?.sessionKind || "").toLowerCase().includes("practical");
  const lines = [
    `${escapeCsvCell("Topic")},${escapeCsvCell(topic)}`,
    `${escapeCsvCell("Class")},${escapeCsvCell(schedule?.classDisplayName || schedule?.className)}`,
    `${escapeCsvCell("Subject")},${escapeCsvCell(schedule?.subjectName)}`,
    `${escapeCsvCell("Date")},${escapeCsvCell(schedule?.date)}`,
    `${escapeCsvCell("Time")},${escapeCsvCell(formatTimeRange12h(schedule?.startTime, schedule?.endTime))}`,
    "",
    [
      "Student Name",
      "Student ID",
      "Roll Number",
      ...(isLab ? ["Batch"] : []),
      "Status",
      "Method",
      "Marked At",
    ].map(escapeCsvCell).join(","),
  ];
  const rows = Array.isArray(schedule?.attendanceList) ? schedule.attendanceList : [];
  let presentCount = 0;
  let absentCount = 0;
  for (const r of rows) {
    const status = String(r.status || "").toUpperCase();
    if (status === "PRESENT") presentCount++;
    else absentCount++;
    lines.push(
      [
        r.studentName,
        r.studentLoginID,
        r.rollNumber || "",
        ...(isLab ? [r.practicalBatchCode || ""] : []),
        r.status,
        formatAttendanceMethodDisplay(r) || "",
        r.markedAt ? new Date(r.markedAt).toLocaleString() : "",
      ].map(escapeCsvCell).join(",")
    );
  }
  // Summary row
  lines.push("");
  lines.push(
    [
      "TOTAL",
      "",
      "",
      ...(isLab ? [""] : []),
      `Present: ${presentCount} | Absent: ${absentCount} | Total: ${rows.length}`,
      "",
      "",
    ].map(escapeCsvCell).join(",")
  );
  const blob = new Blob(["\ufeff", lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `attendance_${schedule?.classId || "class"}_${schedule?.subjectId || "subject"}_${schedule?.date || "date"}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const AttendanceWithQR = () => {
  const navigate = useNavigate();
  const { hasAnyPermission, hasVisiblePermission } = usePermissions();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [sessionDate, setSessionDate] = useState(toDateString(new Date()));
  const [activeMode, setActiveMode] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [roster, setRoster] = useState({ attendance: [], remaining: [], onLeave: [] });
  const [manualStatus, setManualStatus] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [tokenTimeLeft, setTokenTimeLeft] = useState(0);
  const [rotationLoading, setRotationLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [specialSessions, setSpecialSessions] = useState([]);
  const [specialSessionsLoading, setSpecialSessionsLoading] = useState(false);
  const [view, setView] = useState('daily');
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [pendingSessionCreation, setPendingSessionCreation] = useState(null);
  const [fullScreenQR, setFullScreenQR] = useState(false);
  // Tracks the live viewport so the full-screen QR resizes when the browser
  // enters/exits native fullscreen (F11-style) or the window is resized.
  const [viewport, setViewport] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  });
  const fullScreenContainerRef = useRef(null);
  const [viewAttendanceSchedule, setViewAttendanceSchedule] = useState(null);
  // Separate, READ-ONLY live roster view keyed off a RUNNING list card (not the
  // active-session roster, not the completed-card `viewAttendanceSchedule` snapshot).
  const [viewRosterSchedule, setViewRosterSchedule] = useState(null);
  const activeSessionRef = useRef(null);
  const endActiveSessionRef = useRef(null);
  const regenerateActiveRef = useRef(null);
  const rotationFallbackTimerRef = useRef(null);
  const rotationRetryTimerRef = useRef(null);
  const rotationInFlightRef = useRef(false);
  const rotationAttemptRef = useRef(0);
  const autoEndSessionIdRef = useRef(null);
  const endingSessionIdRef = useRef(null);
  const endingPromiseRef = useRef(null);
  const sessionCreationAttemptRef = useRef(0);
  activeSessionRef.current = activeSession;

  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = user?.role?.toLowerCase() || "";
  const normalizedRole = (() => {
    const r = String(userRole || "").toLowerCase().trim();
    if (r === "institute") return "admin";
    if (r === "super admin") return "superadmin";
    return r.replace(/\s+/g, "");
  })();
  const normalizedRolePath = (() => {
    const s = String(userRole || "").trim().toLowerCase().replace(/[_\s]+/g, "");
    if (s === "institute") return "/admin";
    if (s === "superadmin" || s === "superadministrator") return "/superadmin";
    return s ? `/${s}` : "/hod";
  })();
  const isAdminOrInstitute = ['admin', 'institute', 'superadmin'].includes(userRole);
  // HOD gets all-access visibility of attendance (every lecture in its department,
  // regardless of teacher) like an admin — but stays department-locked. It therefore
  // sources its class list from the institute-wide (department-scoped) useClasses
  // path instead of the teacher-only getTeacherClasses endpoint.
  const isAllAccessViewer = isAdminOrInstitute || normalizedRole === 'hod';

  // Institute context: super admin scopes an institute via the selectedInstitute
  // stored in localStorage (dropdown-select flow); everyone else is bound to their
  // own institute. Wrapped defensively so a malformed value never crashes render.
  const selectedInstitute = (() => {
    try {
      const raw = localStorage.getItem("selectedInstitute");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  })();
  const instituteId =
    (normalizedRole === "superadmin" ? selectedInstitute?.id : null) ??
    user?.instituteId ??
    selectedInstitute?.id ??
    null;

  // Teacher/HOD viewers are locked to their own department (Req 4.4). Admin/Super
  // Admin viewers pick a department to scope Class/schedule data (Req 4.3).
  const isDepartmentLockedViewer = ['teacher', 'hod'].includes(userRole);
  const lockedDepartmentId =
    isDepartmentLockedViewer && user?.departmentId != null ? Number(user.departmentId) : null;
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(
    lockedDepartmentId != null ? String(lockedDepartmentId) : ""
  );

  // Department options: only fetched/rendered for admin/super-admin viewers.
  const { data: departmentsResponse } = useDepartments(
    isAdminOrInstitute && instituteId ? { instituteId } : {}
  );
  const departments = useMemo(
    () => (isAdminOrInstitute ? normalizeDepartmentsResponse(departmentsResponse) : []),
    [isAdminOrInstitute, departmentsResponse]
  );

  // Admin/Super-admin class list is scoped by the chosen department. It is only
  // requested once a department is picked (Req 4.3 — no unbounded all-department fetch).
  const adminClassFilters = useMemo(() => {
    if (!isAllAccessViewer) return null;
    if (!instituteId || !selectedDepartmentId) return null;
    return { instituteId, departmentId: Number(selectedDepartmentId) };
  }, [isAllAccessViewer, instituteId, selectedDepartmentId]);
  const { data: adminClassesResponse } = useClasses(adminClassFilters || {});
  const adminScopedClasses = useMemo(
    () => (adminClassFilters ? normalizeAdminClassesResponse(adminClassesResponse) : []),
    [adminClassFilters, adminClassesResponse]
  );

  const canViewAttendance = hasVisiblePermission("attendance.read");
  const canTakeAttendance =
    hasAnyPermission(["attendance.start", "attendance.mark", "attendance.mark_manual"]) &&
    ['teacher', 'hod'].includes(userRole);
  const canManualFromSchedule =
    hasAnyPermission(["attendance.mark_manual", "attendance.start"]) &&
    ['teacher', 'hod'].includes(userRole);

  const getScheduleDateObj = (schedule) => {
    const rawDate = schedule?.date || sessionDate;
    const ymd = String(rawDate || "").slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const dateObj = new Date(`${ymd}T12:00:00+05:30`);
      return Number.isNaN(dateObj.getTime()) ? null : dateObj;
    }
    const dateObj = new Date(rawDate);
    return Number.isNaN(dateObj.getTime()) ? null : dateObj;
  };

  const isScheduleToday = (schedule) => {
    const ymd = String(schedule?.date || sessionDate || "").slice(0, 10);
    return ymd === formatDateIST(new Date());
  };

  const ATTENDANCE_POST_LECTURE_BUFFER_MIN = 15;

  const isInSlotTimeWindow = (schedule) =>
    isSlotInLiveLectureWindowIST({
      date: schedule?.date || sessionDate,
      startTime: schedule?.startTime,
      endTime: schedule?.endTime,
      bufferMinutes: ATTENDANCE_POST_LECTURE_BUFFER_MIN,
    });

  /** COMPLETED / manual-marked only lock the slot after end time + buffer. */
  const isScheduleSlotFinalized = (schedule) => {
    const apiStatus = String(schedule?.status || "").toUpperCase();
    if (["COMPLETED", "ATTENDANCE_MARKED_MANUAL"].includes(apiStatus)) {
      return !isInSlotTimeWindow(schedule);
    }
    return false;
  };

  const isScheduleActiveNow = (schedule) => {
    const apiStatus = String(schedule?.status || "").toUpperCase();
    if (apiStatus === "RUNNING") return true;
    return isInSlotTimeWindow(schedule);
  };

  const isScheduleTimePassed = (schedule) => {
    if (isScheduleToday(schedule)) return false;
    const schedYmd = String(schedule?.date || "").slice(0, 10);
    const todayYmd = formatDateIST(new Date());
    return Boolean(schedYmd && todayYmd && schedYmd < todayYmd);
  };

  const isScheduleUpcoming = (schedule) => {
    if (!isScheduleToday(schedule)) return false;
    const nowSec = getISTTimeParts().secondsOfDay;
    const startSec = timeValueToSecondsOfDay(schedule?.startTime);
    if (startSec == null) return false;
    return nowSec < startSec;
  };

  const formatScheduleDate = (schedule) => {
    const dateObj = getScheduleDateObj(schedule);
    if (!dateObj) return "N/A";
    return dateObj.toLocaleDateString("en-GB");
  };

  const formatLectureTypeLabel = (schedule) => {
    const raw =
      schedule?.lectureModeName ||
      schedule?.lectureType ||
      schedule?.lectureModeCode ||
      "";
    const normalized = String(raw || "").trim().toUpperCase();
    if (!normalized) return "Theory";
    if (normalized.includes("ONLINE")) return "Online";
    if (normalized.includes("LAB")) return "Lab";
    if (normalized.includes("PRACTICAL")) return "Practical";
    if (normalized.includes("THEORY")) return "Theory";
    if (normalized.includes("TUTORIAL")) return "Tutorial";
    if (normalized.includes("SEMINAR")) return "Seminar";
    return raw;
  };

  /** Prefer live time window for chip label when API status is stale or misleading. */
  const resolveScheduleDisplayStatus = (schedule) => {
    const api = String(schedule?.status || "").toUpperCase();
    if (["COMPLETED", "ATTENDANCE_MARKED_MANUAL"].includes(api)) {
      if (isInSlotTimeWindow(schedule)) return "RUNNING";
      return api;
    }
    if (isScheduleActiveNow(schedule)) return "RUNNING";
    if (!isScheduleToday(schedule)) {
      const schedYmd = String(schedule?.date || "").slice(0, 10);
      const todayYmd = toDateString(new Date());
      if (schedYmd && todayYmd && schedYmd < todayYmd) return "NOT_TAKEN";
      if (schedYmd && todayYmd && schedYmd > todayYmd) return "UPCOMING";
    }
    if (isScheduleUpcoming(schedule)) return "UPCOMING";
    if (api === "RUNNING") return "RUNNING";
    if (api === "SESSION_NOT_STARTED" && isScheduleToday(schedule)) return "RUNNING";
    return api || "UPCOMING";
  };

  // Helper function to format schedule status for display
  const formatScheduleStatus = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'Completed';
      case 'ATTENDANCE_MARKED':
        return 'Attendance Marked';
      case 'ATTENDANCE_MARKED_MANUAL':
        return 'Attendance Marked (Manual)';
      case 'NOT_TAKEN':
        return 'Attendance Not Taken';
      case 'RUNNING':
        return 'Running';
      case 'SESSION_NOT_STARTED':
        return 'Attendance Not Taken';
      case 'ATTENDANCE_NOT_MARKED':
        return 'Attendance Not Marked';
      case 'UPCOMING':
        return 'Upcoming';
      case 'SCHEDULED':
        return 'Scheduled';
      default:
        return status || 'Scheduled';
    }
  };

  const resetSession = (expectedSessionId = null) => {
    if (
      expectedSessionId != null &&
      String(activeSessionRef.current?.sessionId || "") !== String(expectedSessionId)
    ) {
      return false;
    }
    sessionCreationAttemptRef.current += 1;
    if (rotationFallbackTimerRef.current) clearTimeout(rotationFallbackTimerRef.current);
    if (rotationRetryTimerRef.current) clearTimeout(rotationRetryTimerRef.current);
    rotationFallbackTimerRef.current = null;
    rotationRetryTimerRef.current = null;
    rotationInFlightRef.current = false;
    rotationAttemptRef.current = 0;
    autoEndSessionIdRef.current = null;
    activeSessionRef.current = null;
    setRotationLoading(false);
    setActiveMode(null);
    setActiveSession(null);
    setRoster({ attendance: [], remaining: [], onLeave: [] });
    setManualStatus({});
    setTimeLeft(0);
    setTokenTimeLeft(0);
    setDialogOpen(false);
    setFullScreenQR(false);
    return true;
  };

  // Enter/exit native browser fullscreen (F11-style) alongside the QR dialog.
  // Wrapped defensively: the Fullscreen API can reject (e.g. no user gesture,
  // iframe policy) — we swallow errors so attendance is never interrupted.
  useEffect(() => {
    const el = fullScreenContainerRef.current;
    if (fullScreenQR) {
      try {
        const target = el || document.documentElement;
        const request =
          target?.requestFullscreen ||
          target?.webkitRequestFullscreen ||
          target?.msRequestFullscreen;
        if (request && !document.fullscreenElement) {
          const result = request.call(target);
          if (result && typeof result.catch === "function") {
            result.catch((err) => {
              console.warn("[AttendanceWithQR] requestFullscreen failed:", err?.message || err);
            });
          }
        }
      } catch (err) {
        console.warn("[AttendanceWithQR] enter fullscreen error:", err?.message || err);
      }
    } else {
      try {
        const exit =
          document.exitFullscreen ||
          document.webkitExitFullscreen ||
          document.msExitFullscreen;
        if (exit && document.fullscreenElement) {
          const result = exit.call(document);
          if (result && typeof result.catch === "function") {
            result.catch(() => {});
          }
        }
      } catch (err) {
        console.warn("[AttendanceWithQR] exit fullscreen error:", err?.message || err);
      }
    }
  }, [fullScreenQR]);

  // Keep the QR sized to the live viewport (resize + fullscreen change). If the
  // user presses Esc/F11 to leave native fullscreen, sync the dialog state back.
  useEffect(() => {
    const syncViewport = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    const handleFullscreenChange = () => {
      syncViewport();
      // Native fullscreen was exited (Esc/F11) while our dialog is still open.
      if (!document.fullscreenElement && fullScreenQR) {
        setFullScreenQR(false);
      }
    };
    window.addEventListener("resize", syncViewport);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    // Sync once on mount / when opening.
    syncViewport();
    return () => {
      window.removeEventListener("resize", syncViewport);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, [fullScreenQR]);

  useEffect(() => {
    // All-access viewers (admin/super-admin/HOD) get their class list from the
    // department-scoped useClasses hook (adminScopedClasses) — skip the teacher-only
    // endpoint, which would return only the classes the viewer personally teaches.
    if (isAllAccessViewer) return;
    const loadClasses = async () => {
      try {
        const res = await getTeacherClasses();
        const normalizedClasses = normalizeClasses(res);
        setClasses(normalizedClasses);
      } catch (e) {
        // Prioritize server error message over axios default message
        const errorMessage = e?.response?.data?.message || e?.message || "Failed to load classes";
        setError(errorMessage);
      }
    };
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllAccessViewer]);

  // For all-access viewers (admin/super-admin/HOD) the effective class options come
  // from the department-scoped useClasses response; for plain teachers they come
  // from state (getTeacherClasses).
  const effectiveClasses = isAllAccessViewer ? adminScopedClasses : classes;

  // When the admin/super-admin viewer switches department, reset a stale class
  // selection so we never keep a class that no longer belongs to the department.
  useEffect(() => {
    if (!isAllAccessViewer) return;
    if (!selectedClassId) return;
    const stillPresent = adminScopedClasses.some(
      (c) => String(c.id) === String(selectedClassId)
    );
    if (!stillPresent) setSelectedClassId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartmentId, adminScopedClasses, isAllAccessViewer]);

  useEffect(() => {
    const loadSubjects = async () => {
      if (!selectedClassId) {
        setSubjects([]);
        setSelectedSubjectId("");
        return;
      }
      try {
        const res = await classSubjectService.getSubjectsByClass(selectedClassId);
        const normalized = normalizeSubjects(res);
        setSubjects(normalized);
        if (normalized.length === 0) setSelectedSubjectId("");
      } catch (e) {
        setSubjects([]);
        setSelectedSubjectId("");
      }
    };
    loadSubjects();
  }, [selectedClassId]);

  useEffect(() => {
    if (!activeSession?.sessionId) return;
    const classMismatch =
      selectedClassId &&
      activeSession.classId != null &&
      Number(selectedClassId) !== Number(activeSession.classId);
    const subjectMismatch =
      selectedSubjectId &&
      activeSession.subjectId != null &&
      Number(selectedSubjectId) !== Number(activeSession.subjectId);
    if (classMismatch || subjectMismatch) {
      endActiveSessionRef.current?.();
      setError(
        "The active attendance session is being ended because the class or subject filter changed."
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId, selectedSubjectId]);

  // Shared schedule fetch used by BOTH the initial-load effect and the live-refresh
  // loop. `silent` refreshes (the live loop) must NOT toggle the loading spinner or
  // blank the list on failure, so the running cards update without flicker.
  const loadSchedules = React.useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setSchedulesLoading(true);
        const params = { date: sessionDate, view };
        if (instituteId) params.instituteId = instituteId;
        if (selectedDepartmentId) params.departmentId = Number(selectedDepartmentId);
        if (selectedClassId) params.classId = selectedClassId;
        if (selectedSubjectId) params.subjectId = selectedSubjectId;
        // Smart Attendance is an all-access view. A HOD must opt into department-wide
        // visibility here (see /schedules `scope` flag) — without it the backend
        // returns only the HOD's own teaching slots (the mobile "My Lectures" default).
        // admin/institute/superadmin are already all-access server-side, so the flag
        // is only needed for HOD.
        if (normalizedRole === 'hod') params.scope = 'all';
        const res = await scheduleService.getTimetable(params);
        const scheduleData = res?.data?.data || [];
        setSchedules(Array.isArray(scheduleData) ? scheduleData : []);
      } catch (e) {
        // On a background (silent) refresh failure keep the previous schedules so the
        // list never blanks out mid-lecture. Only the initial load clears on error.
        if (!silent) setSchedules([]);
      } finally {
        if (!silent) setSchedulesLoading(false);
      }
    },
    [sessionDate, view, selectedClassId, selectedSubjectId, selectedDepartmentId, instituteId, normalizedRole]
  );

  const loadSchedulesRef = useRef(loadSchedules);
  loadSchedulesRef.current = loadSchedules;

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  // Is there at least one RUNNING lecture currently shown in the list? Reuses the
  // existing display-status / slot-window helpers so status logic stays in one place.
  // When nothing is running we must NOT poll (avoid needless requests).
  const hasRunningSchedule = useMemo(() => {
    if (!Array.isArray(schedules) || schedules.length === 0) return false;
    try {
      return schedules.some(
        (schedule) =>
          resolveScheduleDisplayStatus(schedule) === "RUNNING" || isInSlotTimeWindow(schedule)
      );
    } catch (e) {
      return false;
    }
    // resolveScheduleDisplayStatus / isInSlotTimeWindow close over sessionDate; they are
    // stable per-render component fns, so schedules + sessionDate drive recomputation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedules, sessionDate]);

  // Live-updating schedule cards: while any lecture is RUNNING, silently re-fetch the
  // same schedule data the initial load uses (every 5s to match the roster loop), plus
  // a debounced refresh on each `attendance_marked` socket event for near-instant
  // Total/Present/Absent updates — no manual page refresh needed. List cards do not
  // carry a session id, so the socket refresh is session-agnostic (any running lecture
  // triggers it) and the 5s poll is the guaranteed safety path.
  useEffect(() => {
    if (!hasRunningSchedule) return undefined;

    let disposed = false;
    let refreshInFlight = false;
    let refreshQueued = false;
    let debounceTimer = null;

    const refreshSchedules = async () => {
      if (disposed) return;
      if (refreshInFlight) {
        refreshQueued = true;
        return;
      }
      refreshInFlight = true;
      try {
        await loadSchedulesRef.current?.({ silent: true });
      } catch (e) {
        // loadSchedules already swallows errors silently; this guard prevents any
        // unhandled rejection from the live loop.
      } finally {
        refreshInFlight = false;
        if (refreshQueued && !disposed) {
          refreshQueued = false;
          void refreshSchedules();
        }
      }
    };

    const pollTimer = setInterval(() => void refreshSchedules(), 5000);

    const socket = getAppSocket();
    if (!socket) {
      return () => {
        disposed = true;
        clearInterval(pollTimer);
      };
    }

    const attendanceMarkedHandler = () => {
      if (disposed || debounceTimer) return;
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        void refreshSchedules();
      }, 200);
    };
    socket.on?.("attendance_marked", attendanceMarkedHandler);

    return () => {
      disposed = true;
      clearInterval(pollTimer);
      if (debounceTimer) clearTimeout(debounceTimer);
      socket.off?.("attendance_marked", attendanceMarkedHandler);
    };
  }, [hasRunningSchedule]);

  const loadSpecialSessions = React.useCallback(async () => {
    try {
      setSpecialSessionsLoading(true);
      const res = await specialSessionService.visible({ date: sessionDate, t: Date.now() });
      const data = res?.data?.data || [];
      setSpecialSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      setSpecialSessions([]);
    } finally {
      setSpecialSessionsLoading(false);
    }
  }, [sessionDate]);

  useEffect(() => {
    loadSpecialSessions();
  }, [loadSpecialSessions]);

  useEffect(() => {
    const onFocus = () => loadSpecialSessions();
    const onMarked = () => loadSpecialSessions();
    window.addEventListener("focus", onFocus);
    window.addEventListener("special-session-attendance-marked", onMarked);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("special-session-attendance-marked", onMarked);
    };
  }, [loadSpecialSessions]);

  const navPeriod = (dir) => {
    setSessionDate((prev) => {
      const ymd = prev && /^\d{4}-\d{2}-\d{2}$/.test(prev) ? prev : toDateString(new Date());
      const base = new Date(`${ymd}T12:00:00`);
      if (view === "monthly") base.setMonth(base.getMonth() + dir);
      else if (view === "weekly") base.setDate(base.getDate() + 7 * dir);
      else base.setDate(base.getDate() + dir);
      return toDateString(base);
    });
  };

  const formatGeoError = (error) => {
    if (!error) return 'Unable to read your GPS coordinates. Please allow location for this site and try again.';
    if (error.code === 1) {
      return 'Location permission denied. Allow location for this website in the browser address bar, then refresh and try again.';
    }
    if (error.code === 2) {
      return 'Location unavailable. On desktop, enable Location Services in system settings (Privacy & Security) and ensure Wi‑Fi is on.';
    }
    if (error.code === 3) {
      return 'Location request timed out. Move closer to a window, check Wi‑Fi/GPS, and try again.';
    }
    return error.message || 'Unable to read your GPS coordinates.';
  };

  const hasValidGeo = (geo) =>
    geo &&
    geo.lat != null &&
    geo.lng != null &&
    Number.isFinite(Number(geo.lat)) &&
    Number.isFinite(Number(geo.lng));

  const resolveGeo = async () => {
    if (!navigator.geolocation) {
      return { lat: null, lng: null, error: 'Geolocation is not supported in this browser.' };
    }

    const tryGet = (options) =>
      new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) =>
            resolve({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            }),
          (error) => resolve({ error }),
          options
        );
      });

    // High accuracy (GPS) first — this position becomes the geofence center for all students.
    // Fallback to network/Wi-Fi only if GPS fails.
    const strategies = [
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 10000 },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 },
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 300000 },
    ];

    let lastError = null;
    for (const options of strategies) {
      const result = await tryGet(options);
      if (hasValidGeo(result)) {
        return { lat: Number(result.lat), lng: Number(result.lng) };
      }
      if (result?.error) lastError = result.error;
    }

    return { lat: null, lng: null, error: formatGeoError(lastError) };
  };

  const checkLocationPermission = async () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ granted: false, error: 'Geolocation is not supported in this browser.' });
        return;
      }

      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'granted') {
          resolve({ granted: true });
        } else if (result.state === 'denied') {
          resolve({ granted: false, error: 'Location permission denied. Please enable location services to take attendance.' });
        } else {
          // Prompt for permission
          navigator.geolocation.getCurrentPosition(
            () => resolve({ granted: true }),
            (error) => {
              let errorMsg = 'Location permission denied. ';
              if (error.code === 1) {
                errorMsg += 'Please enable location permissions in your browser settings.';
              }
              resolve({ granted: false, error: errorMsg });
            },
            { enableHighAccuracy: true, timeout: 10000 }
          );
        }
      }).catch(() => {
        // Fallback for browsers that don't support permissions API
        navigator.geolocation.getCurrentPosition(
          () => resolve({ granted: true }),
          (error) => {
            // On Mac, code 2 means Location Services disabled at system level — try without high accuracy
            if (error.code === 2) {
              navigator.geolocation.getCurrentPosition(
                () => resolve({ granted: true }),
                (fallbackError) => {
                  let errorMsg = 'Location access unavailable. ';
                  if (fallbackError.code === 1) {
                    errorMsg += 'Please enable location permissions in your browser settings.';
                  } else {
                    errorMsg += 'On Mac, enable Location Services in System Settings > Privacy & Security.';
                  }
                  resolve({ granted: false, error: errorMsg });
                },
                { enableHighAccuracy: false, timeout: 8000 }
              );
              return;
            }
            let errorMsg = 'Location permission denied. ';
            if (error.code === 1) {
              errorMsg += 'Please enable location permissions in your browser settings.';
            }
            resolve({ granted: false, error: errorMsg });
          },
          { enableHighAccuracy: true, timeout: 8000 }
        );
      });
    });
  };

  const buildManualStatusFromRoster = (attendance, remaining, onLeave) => {
    const next = {};
    attendance.forEach((s) => {
      const raw = String(s?.attendance?.status || "").toUpperCase();
      if (!raw) return;
      next[s.id] = raw === "ON_LEAVE" ? "LEAVE" : raw;
    });
    onLeave.forEach((s) => {
      if (next[s.id]) return;
      const raw = String(s?.attendance?.status || "").toUpperCase();
      if (raw && raw !== "PRESENT") {
        next[s.id] = raw === "ON_LEAVE" ? "LEAVE" : raw;
      } else if (s?.leave) {
        next[s.id] = "LEAVE";
      }
    });
    return next;
  };

  const fetchRoster = async (classId, subjectId, rosterSessionId, rosterSlotId, rosterDate = sessionDate) => {
    if (!classId || !rosterDate) return;
    const expectedSessionId = rosterSessionId != null ? String(rosterSessionId) : null;
    try {
      const res = await attendanceService.getRoster(classId, {
        date: rosterDate,
        subjectId: subjectId || undefined,
        timeTableSlotId: rosterSlotId || activeSessionRef.current?.timeTableSlotId || undefined,
        sessionId: rosterSessionId || activeSessionRef.current?.sessionId || undefined,
        page: 1,
        limit: 500,
      });
      if (
        expectedSessionId &&
        String(activeSessionRef.current?.sessionId || "") !== expectedSessionId
      ) {
        return;
      }
      const data = res?.data?.data?.data || {};
      const attendance = data.attendance || [];
      const remaining = data.remaining || [];
      const onLeave = data.onLeave || [];
      setRoster({ attendance, remaining, onLeave });
      setManualStatus(buildManualStatusFromRoster(attendance, remaining, onLeave));
    } catch (e) {
      if (
        expectedSessionId &&
        String(activeSessionRef.current?.sessionId || "") !== expectedSessionId
      ) {
        return;
      }
      const errorMessage = e?.response?.data?.message || e?.message || "Failed to load roster";
      setError(errorMessage);
    }
  };

  const fetchRosterRef = useRef(fetchRoster);
  fetchRosterRef.current = fetchRoster;
  const applyTokenPairRef = useRef(null);

  // Poll roster every 5s during active QR/OTP session to show live count
  useEffect(() => {
    if (!activeSession?.sessionId || !dialogOpen || activeMode === "MANUAL") return;
    const interval = setInterval(() => {
      fetchRosterRef.current(activeSession.classId, activeSession.subjectId, activeSession.sessionId);
    }, 5000);
    return () => clearInterval(interval);
  }, [activeSession?.sessionId, activeSession?.classId, activeSession?.subjectId, dialogOpen, activeMode]);


  const applyTokenPair = (payload, expectedSessionId, clockSample = {}) => {
    const current = activeSessionRef.current;
    const expectedId = String(expectedSessionId || "");
    if (!current?.sessionId || String(current.sessionId) !== expectedId) return false;

    const pair = normalizeAttendancePair(payload, {
      requireLectureContext: Boolean(current.timeTableSlotId),
      fallbackOffsetMs: Number(current.serverTimeOffsetMs || 0),
      ...clockSample,
    });
    if (
      !pair ||
      pair.sessionId !== expectedId ||
      !isAttendancePairFresh(pair) ||
      (current.lectureDate && pair.lectureDate !== String(current.lectureDate).slice(0, 10)) ||
      (current.timeTableSlotId && pair.timeTableSlotId !== String(current.timeTableSlotId))
    ) {
      return false;
    }
    if (String(activeSessionRef.current?.sessionId || "") !== expectedId) return false;

    const generationOrder = compareAttendancePairGeneration(pair, current);
    if (generationOrder === "stale") return false;

    if (rotationFallbackTimerRef.current) clearTimeout(rotationFallbackTimerRef.current);
    if (rotationRetryTimerRef.current) clearTimeout(rotationRetryTimerRef.current);
    rotationFallbackTimerRef.current = null;
    rotationRetryTimerRef.current = null;
    rotationInFlightRef.current = false;
    rotationAttemptRef.current = 0;
    setRotationLoading(false);
    setError("");

    // A repeated ack/broadcast for the exact token IDs is successful, but must not
    // perturb the accepted generation or its established clock estimate.
    if (generationOrder === "duplicate") return true;

    const currentRttMs = current.clockSampleRttMs == null
      ? null
      : Number(current.clockSampleRttMs);
    const incomingRttMs = pair.clockSampleRttMs == null
      ? null
      : Number(pair.clockSampleRttMs);
    const useIncomingClock = incomingRttMs != null && Number.isFinite(incomingRttMs) &&
      (currentRttMs == null || !Number.isFinite(currentRttMs) || incomingRttMs < currentRttMs);
    const nextSession = {
      ...current,
      ...pair,
      serverTimeOffsetMs: useIncomingClock
        ? pair.serverTimeOffsetMs
        : Number(current.serverTimeOffsetMs || 0),
      clockSampleRttMs: useIncomingClock
        ? pair.clockSampleRttMs
        : (Number.isFinite(currentRttMs) ? currentRttMs : null),
    };
    activeSessionRef.current = nextSession;
    setActiveSession((previous) =>
      previous && String(previous.sessionId) === expectedId ? nextSession : previous
    );
    return true;
  };
  applyTokenPairRef.current = applyTokenPair;

  const handleTerminalRotationFailure = (failure, expectedSessionId) => {
    if (!isTerminalRotationFailure(failure)) return false;
    const expectedId = String(expectedSessionId || "");
    if (String(activeSessionRef.current?.sessionId || "") !== expectedId) return true;
    resetSession(expectedId);
    setError("This attendance session has ended. Start a new session to continue.");
    return true;
  };

  const endActiveSession = () => {
    const session = activeSessionRef.current;
    if (!session?.sessionId) {
      setError("No active session found");
      return Promise.resolve(true);
    }

    const sessionId = String(session.sessionId);
    if (
      endingSessionIdRef.current === sessionId &&
      endingPromiseRef.current
    ) {
      return endingPromiseRef.current;
    }

    endingSessionIdRef.current = sessionId;
    setLoading(true);
    const endPromise = (async () => {
      try {
        await attendanceService.endSession(sessionId);
        return resetSession(sessionId);
      } catch (e) {
        if (String(activeSessionRef.current?.sessionId || "") !== sessionId) return false;
        autoEndSessionIdRef.current = null;
        if (isTerminalRotationFailure(e)) {
          resetSession(sessionId);
          setError("This attendance session has already ended.");
          return true;
        }
        const errorMessage = e?.response?.data?.message || e?.message || "Failed to end session";
        setError(errorMessage);
        return false;
      } finally {
        if (endingSessionIdRef.current === sessionId) {
          endingSessionIdRef.current = null;
          endingPromiseRef.current = null;
          setLoading(false);
        }
      }
    })();
    endingPromiseRef.current = endPromise;
    return endPromise;
  };
  endActiveSessionRef.current = endActiveSession;

  const runAfterEndingActiveSession = async (action) => {
    if (activeSessionRef.current?.sessionId) {
      const ended = await endActiveSessionRef.current?.();
      if (!ended) return false;
    }
    action();
    return true;
  };

  useEffect(() => {
    const sessionId = activeSession?.sessionId;
    const expiryMs = parseTimestampMs(activeSession?.expiryTime);
    if (!sessionId || expiryMs == null) {
      setTimeLeft(0);
      return undefined;
    }
    const serverTimeOffsetMs = Number(activeSession?.serverTimeOffsetMs || 0);
    autoEndSessionIdRef.current = null;
    const update = () => {
      const serverNowMs = Date.now() + serverTimeOffsetMs;
      const remaining = Math.max(0, Math.ceil((expiryMs - serverNowMs) / 1000));
      setTimeLeft(remaining);
      if (remaining === 0 && autoEndSessionIdRef.current !== String(sessionId)) {
        autoEndSessionIdRef.current = String(sessionId);
        endActiveSessionRef.current?.();
      }
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [activeSession?.expiryTime, activeSession?.serverTimeOffsetMs, activeSession?.sessionId]);

  const regenerateActive = () => {
    const session = activeSessionRef.current;
    if (!session?.sessionId || activeMode === "MANUAL" || rotationInFlightRef.current) return;

    if (rotationFallbackTimerRef.current) clearTimeout(rotationFallbackTimerRef.current);
    if (rotationRetryTimerRef.current) clearTimeout(rotationRetryTimerRef.current);
    rotationInFlightRef.current = true;
    setRotationLoading(true);
    const sessionId = String(session.sessionId);
    const socket = getAppSocket();
    if (socket?.connected) {
      const requestStartedAtMs = Date.now();
      socket.emit(
        "regenerate_attendance_session",
        { sessionId, mode: "BOTH" },
        (payload) => {
          const responseReceivedAtMs = Date.now();
          if (String(activeSessionRef.current?.sessionId || "") !== sessionId) return;
          if (handleTerminalRotationFailure(payload, sessionId)) return;
          applyTokenPairRef.current?.(payload, sessionId, {
            requestStartedAtMs,
            responseReceivedAtMs,
          });
        }
      );
    }

    const fallbackDelayMs = rotationAttemptRef.current === 0
      ? 3000
      : Math.min(3000 * (2 ** Math.min(rotationAttemptRef.current, 2)), 12000);
    rotationFallbackTimerRef.current = setTimeout(async () => {
      if (String(activeSessionRef.current?.sessionId || "") !== sessionId) return;
      try {
        const {
          response,
          requestStartedAtMs,
          responseReceivedAtMs,
        } = await requestWithClockSample(() => attendanceService.rotateSession(sessionId));
        if (String(activeSessionRef.current?.sessionId || "") !== sessionId) return;
        const applied = applyTokenPairRef.current?.(response, sessionId, {
          requestStartedAtMs,
          responseReceivedAtMs,
        });
        if (!applied) throw new Error("Incomplete or stale attendance token pair received");
      } catch (e) {
        if (String(activeSessionRef.current?.sessionId || "") !== sessionId) return;
        if (handleTerminalRotationFailure(e, sessionId)) return;
        rotationInFlightRef.current = false;
        setRotationLoading(false);
        rotationAttemptRef.current += 1;
        if (rotationAttemptRef.current === 1) {
          setError(e?.response?.data?.message || "Attendance code refresh is delayed. Retrying automatically.");
        }
        const retryDelayMs = Math.min(3000 * (2 ** Math.min(rotationAttemptRef.current, 2)), 12000);
        rotationRetryTimerRef.current = setTimeout(() => {
          if (String(activeSessionRef.current?.sessionId || "") === sessionId) {
            regenerateActiveRef.current?.();
          }
        }, retryDelayMs);
      }
    }, fallbackDelayMs);
  };
  regenerateActiveRef.current = regenerateActive;

  useEffect(() => {
    const tokenExpiryMs = parseTimestampMs(activeSession?.tokenValidUntil);
    if (!activeSession?.sessionId || tokenExpiryMs == null || activeMode === "MANUAL") {
      setTokenTimeLeft(0);
      return undefined;
    }
    const serverTimeOffsetMs = Number(activeSession?.serverTimeOffsetMs || 0);
    const update = () => {
      const serverNowMs = Date.now() + serverTimeOffsetMs;
      const remaining = Math.max(0, Math.ceil((tokenExpiryMs - serverNowMs) / 1000));
      setTokenTimeLeft(remaining);
      if (remaining === 0) regenerateActiveRef.current?.();
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [activeMode, activeSession?.serverTimeOffsetMs, activeSession?.sessionId, activeSession?.tokenValidUntil]);

  useEffect(() => {
    const session = activeSessionRef.current;
    if (!session?.sessionId || activeMode === "MANUAL") return undefined;
    const sessionId = String(session.sessionId);
    let disposed = false;
    let rosterRequestInFlight = false;
    let rosterRefreshQueued = false;
    let attendanceRefreshTimer = null;

    const refreshRoster = async () => {
      if (disposed || String(activeSessionRef.current?.sessionId || "") !== sessionId) return;
      if (rosterRequestInFlight) {
        rosterRefreshQueued = true;
        return;
      }
      rosterRequestInFlight = true;
      try {
        await fetchRosterRef.current?.(
          session.classId,
          session.subjectId,
          sessionId,
          session.timeTableSlotId,
          session.lectureDate || sessionDate
        );
      } finally {
        rosterRequestInFlight = false;
        if (rosterRefreshQueued && !disposed) {
          rosterRefreshQueued = false;
          void refreshRoster();
        }
      }
    };

    void refreshRoster();
    const rosterTimer = setInterval(() => void refreshRoster(), 5000);
    const socket = getAppSocket();
    if (!socket) {
      return () => {
        disposed = true;
        clearInterval(rosterTimer);
      };
    }

    const joinSession = () => socket.emit("join_attendance_session", { sessionId });
    const regeneratedHandler = (payload) => {
      if (String(payload?.sessionId || "") !== sessionId) return;
      applyTokenPairRef.current?.(payload, sessionId);
    };
    const attendanceMarkedHandler = (payload) => {
      if (String(payload?.sessionId || "") !== sessionId || attendanceRefreshTimer) return;
      attendanceRefreshTimer = setTimeout(() => {
        attendanceRefreshTimer = null;
        void refreshRoster();
      }, 150);
    };
    joinSession();
    socket.on("connect", joinSession);
    socket.on("attendance_session_regenerated", regeneratedHandler);
    socket.on("attendance_marked", attendanceMarkedHandler);

    return () => {
      disposed = true;
      clearInterval(rosterTimer);
      if (attendanceRefreshTimer) clearTimeout(attendanceRefreshTimer);
      socket.off("connect", joinSession);
      socket.off("attendance_session_regenerated", regeneratedHandler);
      socket.off("attendance_marked", attendanceMarkedHandler);
      socket.emit("leave_attendance_session", { sessionId });
    };
  }, [activeMode, activeSession?.sessionId, activeSession?.classId, activeSession?.subjectId, activeSession?.timeTableSlotId, activeSession?.lectureDate, sessionDate]);

  const proceedWithSessionCreation = async (
    mode,
    targetClassId,
    targetSubjectId,
    geoOverride,
    timeTableSlotId = null,
    lectureDate = sessionDate
  ) => {
    const creationAttemptId = ++sessionCreationAttemptRef.current;
    try {
      setLoading(true);
      const geo = geoOverride || (await resolveGeo());
      if (!hasValidGeo(geo)) {
        setError(geo?.error || 'Unable to read your location. Please allow location access and try again.');
        setPendingSessionCreation({ mode, classId: targetClassId, subjectId: targetSubjectId, timeTableSlotId, lectureDate });
        setLocationDialogOpen(true);
        return;
      }

      const payload = {
        classId: Number(targetClassId),
        subjectId: targetSubjectId ? Number(targetSubjectId) : undefined,
        durationMinutes: 10,
        sessionDate: lectureDate,
        lectureDate,
        lat: geo.lat,
        lng: geo.lng,
        // Guard against synthetic/non-numeric ids (e.g. converted virtual slot `conv_...`):
        // never send NaN — omit the field so the backend infers the slot from class/date/time.
        ...(timeTableSlotId != null && Number.isFinite(Number(timeTableSlotId))
          ? { timeTableSlotId: Number(timeTableSlotId) }
          : {}),
      };

      let res;
      let clockSample = {};
      if (mode === "QR") {
        const timed = await requestWithClockSample(() => attendanceService.createQrSession(payload));
        res = timed.response;
        clockSample = timed;
      } else if (mode === "OTP") {
        const timed = await requestWithClockSample(() => attendanceService.createOtpSession(payload));
        res = timed.response;
        clockSample = timed;
      } else {
        res = await attendanceService.startManualSession(payload);
      }

      const data = res?.data?.data || {};
      let nextSession;
      if (mode === "MANUAL") {
        nextSession = {
          sessionId: data.sessionId,
          publicId: null,
          otp: null,
          classId: Number(targetClassId),
          subjectId: targetSubjectId ? Number(targetSubjectId) : undefined,
          timeTableSlotId: data.timeTableSlotId || (timeTableSlotId ? Number(timeTableSlotId) : undefined),
          lectureDate: data.lectureDate || lectureDate,
          sessionTime: data.sessionTime || null,
          expiryTime: toIsoTimestamp(data.expiryTime),
          tokenValidUntil: null,
          serverTimeOffsetMs: 0,
        };
      } else {
        const pair = normalizeAttendancePair(res, {
          requireLectureContext: Boolean(timeTableSlotId),
          requestStartedAtMs: clockSample.requestStartedAtMs,
          responseReceivedAtMs: clockSample.responseReceivedAtMs,
        });
        if (!pair || !isAttendancePairFresh(pair)) {
          throw new Error("Backend returned an incomplete or expired QR/OTP attendance pair");
        }
        nextSession = {
          ...pair,
          classId: Number(targetClassId),
          subjectId: targetSubjectId ? Number(targetSubjectId) : undefined,
          timeTableSlotId: pair.timeTableSlotId ? Number(pair.timeTableSlotId) : undefined,
          lectureDate: pair.lectureDate || lectureDate,
        };
      }

      if (mode !== "MANUAL") {
        const currentPair = activeSessionRef.current;
        if (currentPair && String(currentPair.sessionId) === String(nextSession.sessionId)) {
          const generationOrder = compareAttendancePairGeneration(nextSession, currentPair);
          if (generationOrder === "stale") return;
          if (generationOrder === "duplicate") nextSession = currentPair;
        }
      }
      if (sessionCreationAttemptRef.current !== creationAttemptId) return;
      setActiveMode(mode === "MANUAL" ? mode : "BOTH");
      activeSessionRef.current = nextSession;
      setActiveSession(nextSession);

      if (mode !== "MANUAL") {
        setDialogOpen(true);
      }

      await fetchRoster(
        Number(targetClassId),
        targetSubjectId ? Number(targetSubjectId) : undefined,
        nextSession.sessionId,
        nextSession.timeTableSlotId,
        nextSession.lectureDate
      );
    } catch (e) {
      if (sessionCreationAttemptRef.current !== creationAttemptId) return;
      // Prioritize server error message over axios default message
      const apiErrorMessage = e?.response?.data?.message || e?.message || "Failed to create attendance session";
      setError(apiErrorMessage);
    } finally {
      if (sessionCreationAttemptRef.current === creationAttemptId) setLoading(false);
    }
  };

  const createSession = async (mode, classId = null, subjectId = null) => {
    setError("");
    const targetClassId = classId || selectedClassId;
    const targetSubjectId = subjectId || selectedSubjectId;

    if (!targetClassId) {
      setError("Please select a class");
      return;
    }

    setLoading(true);
    const geo = await resolveGeo();
    if (!hasValidGeo(geo)) {
      setPendingSessionCreation({ mode, classId: targetClassId, subjectId: targetSubjectId });
      setLocationDialogOpen(true);
      setError(geo?.error || 'Location is required to start attendance.');
      setLoading(false);
      return;
    }

    await proceedWithSessionCreation(mode, targetClassId, targetSubjectId, geo);
  };

  const proceedWithBothSessionsCreation = async (
    classId,
    subjectId,
    geoOverride,
    timeTableSlotId = null,
    lectureDate = sessionDate
  ) => {
    const creationAttemptId = ++sessionCreationAttemptRef.current;
    try {
      setLoading(true);
      const geo = geoOverride || (await resolveGeo());
      const effectiveLectureDate = String(lectureDate || sessionDate).slice(0, 10);
      if (!hasValidGeo(geo)) {
        setError(geo?.error || 'Unable to read your location. Please allow location access and try again.');
        setPendingSessionCreation({ mode: 'BOTH', classId, subjectId, timeTableSlotId, lectureDate: effectiveLectureDate });
        setLocationDialogOpen(true);
        return;
      }

      const payload = {
        classId: Number(classId),
        subjectId: subjectId ? Number(subjectId) : undefined,
        durationMinutes: 10,
        sessionDate: effectiveLectureDate,
        lectureDate: effectiveLectureDate,
        lat: geo.lat,
        lng: geo.lng,
        // Guard against synthetic/non-numeric ids (e.g. converted virtual slot `conv_...`):
        // never send NaN — omit the field so the backend infers the slot from class/date/time.
        ...(timeTableSlotId != null && Number.isFinite(Number(timeTableSlotId))
          ? { timeTableSlotId: Number(timeTableSlotId) }
          : {}),
      };

      let synchronizedPair = null;
      let lastPairError = null;
      for (let attempt = 0; attempt < 2 && !synchronizedPair; attempt += 1) {
        try {
          const [qrTimed, otpTimed] = await Promise.all([
            requestWithClockSample(() => attendanceService.createQrSession(payload)),
            requestWithClockSample(() => attendanceService.createOtpSession(payload)),
          ]);
          if (sessionCreationAttemptRef.current !== creationAttemptId) return;
          const requireLectureContext = Boolean(timeTableSlotId);
          const qrPair = normalizeAttendancePair(qrTimed.response, {
            requireLectureContext,
            requestStartedAtMs: qrTimed.requestStartedAtMs,
            responseReceivedAtMs: qrTimed.responseReceivedAtMs,
          });
          const otpPair = normalizeAttendancePair(otpTimed.response, {
            requireLectureContext,
            requestStartedAtMs: otpTimed.requestStartedAtMs,
            responseReceivedAtMs: otpTimed.responseReceivedAtMs,
          });
          const expectedContextMatches =
            (!timeTableSlotId || (
              qrPair?.timeTableSlotId === String(timeTableSlotId) &&
              otpPair?.timeTableSlotId === String(timeTableSlotId)
            )) &&
            qrPair?.lectureDate === effectiveLectureDate &&
            otpPair?.lectureDate === effectiveLectureDate;

          if (
            attendancePairsMatch(qrPair, otpPair) &&
            isAttendancePairFresh(qrPair) &&
            isAttendancePairFresh(otpPair) &&
            expectedContextMatches
          ) {
            synchronizedPair =
              Number(otpPair.clockSampleRttMs) <= Number(qrPair.clockSampleRttMs)
                ? otpPair
                : qrPair;
          } else if (
            qrPair && otpPair &&
            String(qrPair.sessionId) === String(otpPair.sessionId) &&
            qrPair.sessionId !== "" &&
            isAttendancePairFresh(qrPair) &&
            isAttendancePairFresh(otpPair) &&
            expectedContextMatches
          ) {
            // Both pairs are valid, fresh, and from the same session but a token rotation
            // happened between the two serialized requests. Use the newer generation.
            const qrIssuedMs = parseTimestampMs(qrPair.tokenIssuedAt) ?? 0;
            const otpIssuedMs = parseTimestampMs(otpPair.tokenIssuedAt) ?? 0;
            synchronizedPair = otpIssuedMs >= qrIssuedMs ? otpPair : qrPair;
          } else {
            lastPairError = new Error(
              "QR and OTP must each return the same complete synchronized attendance pair"
            );
          }
        } catch (attemptError) {
          lastPairError = attemptError;
        }
      }
      if (!synchronizedPair) throw lastPairError || new Error("Failed to create synchronized attendance codes");
      const currentPair = activeSessionRef.current;
      if (currentPair && String(currentPair.sessionId) === synchronizedPair.sessionId) {
        const generationOrder = compareAttendancePairGeneration(synchronizedPair, currentPair);
        if (generationOrder === "stale") return;
        if (generationOrder === "duplicate") synchronizedPair = currentPair;
      }

      const nextSession = {
        ...synchronizedPair,
        classId: Number(classId),
        subjectId: subjectId ? Number(subjectId) : undefined,
        timeTableSlotId: synchronizedPair.timeTableSlotId
          ? Number(synchronizedPair.timeTableSlotId)
          : undefined,
        lectureDate: synchronizedPair.lectureDate,
      };
      if (sessionCreationAttemptRef.current !== creationAttemptId) return;
      setActiveMode("BOTH");
      activeSessionRef.current = nextSession;
      setActiveSession(nextSession);
      setDialogOpen(true);

      await fetchRoster(
        Number(classId),
        subjectId ? Number(subjectId) : undefined,
        synchronizedPair.sessionId,
        synchronizedPair.timeTableSlotId,
        synchronizedPair.lectureDate
      );
    } catch (e) {
      if (sessionCreationAttemptRef.current !== creationAttemptId) return;
      const apiErrorMessage = e?.response?.data?.message || e?.message || "Failed to create attendance sessions";
      setError(apiErrorMessage);
    } finally {
      if (sessionCreationAttemptRef.current === creationAttemptId) setLoading(false);
    }
  };

  // Converted (virtual) slots carry a synthetic string id like `conv_{overrideId}_{index}` and
  // have `isConverted: true`. They have NO backing TimeTableSlot row, so the synthetic id must
  // NEVER be sent to the backend as a real `timeTableSlotId` (coercing it with Number(...) yields
  // NaN and breaks slot resolution). For converted slots the backend resolves the effective
  // lecture type / batch via classId + lectureDate + startTime + endTime + teacherId (see
  // resolveVirtualSlotForAttendance, Task 9), so we send null here and let it infer.
  const resolveRealSlotIdForStart = (schedule) => {
    if (!schedule || schedule.isConverted) return null;
    const raw = schedule.timeTableSlotId;
    if (raw == null) return null;
    // Only pass ids that are genuine numeric slot ids; guard any non-numeric/synthetic id.
    return Number.isFinite(Number(raw)) ? raw : null;
  };

  const startAttendanceForSchedule = async (schedule) => {
    if (!schedule?.classId) {
      setError("Class ID is required");
      return;
    }
    if (activeSessionRef.current?.sessionId) {
      const ended = await endActiveSessionRef.current?.();
      if (!ended) return;
    }
    if (schedule.classId) setSelectedClassId(String(schedule.classId));
    if (schedule.subjectId) setSelectedSubjectId(String(schedule.subjectId));
    await createBothSessions(
      schedule.classId,
      schedule.subjectId,
      resolveRealSlotIdForStart(schedule),
      schedule.date || sessionDate
    );
  };

  const createBothSessions = async (classId, subjectId, timeTableSlotId = null, lectureDate = sessionDate) => {
    setError("");
    if (!classId) {
      setError("Class ID is required");
      return;
    }

    setLoading(true);
    const geo = await resolveGeo();
    if (!hasValidGeo(geo)) {
      setPendingSessionCreation({ mode: 'BOTH', classId, subjectId, timeTableSlotId, lectureDate });
      setLocationDialogOpen(true);
      setError(geo?.error || 'Location is required to start attendance.');
      setLoading(false);
      return;
    }

    await proceedWithBothSessionsCreation(classId, subjectId, geo, timeTableSlotId, lectureDate);
  };

  const submitManual = async () => {
    if (!activeSession?.sessionId) {
      setError("No active manual session found");
      return;
    }

    if (
      selectedClassId &&
      activeSession.classId != null &&
      Number(selectedClassId) !== Number(activeSession.classId)
    ) {
      setError("Selected class does not match the active session. End the session or select the correct class.");
      return;
    }
    if (
      selectedSubjectId &&
      activeSession.subjectId != null &&
      Number(selectedSubjectId) !== Number(activeSession.subjectId)
    ) {
      setError("Selected subject does not match the active session. End the session or select the correct subject.");
      return;
    }

    const allRows = [...roster.attendance, ...roster.remaining, ...roster.onLeave];
    const unmarked = allRows.filter((s) => !manualStatus[s.id]);
    if (unmarked.length > 0) {
      setError("Please select attendance status for all students before submitting.");
      return;
    }
    const attendances = allRows.map((s) => ({
      studentId: s.id,
      status: manualStatus[s.id],
    }));

    try {
      setLoading(true);
      await attendanceService.markBulkAttendance({
        sessionId: activeSession.sessionId,
        classId: activeSession.classId,
        subjectId: activeSession.subjectId,
        timeTableSlotId: activeSession.timeTableSlotId,
        attendances,
      });
      await fetchRoster(activeSession.classId, activeSession.subjectId);
    } catch (e) {
      // Prioritize server error message over axios default message
      const errorMessage = e?.response?.data?.message || e?.message || "Failed to submit manual attendance";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const totalPresent = useMemo(
    () => [...roster.attendance, ...roster.remaining, ...roster.onLeave].filter((s) => manualStatus[s.id] === "PRESENT").length,
    [roster, manualStatus]
  );

  const totalAbsent = useMemo(
    () => [...roster.attendance, ...roster.remaining, ...roster.onLeave].filter((s) => manualStatus[s.id] === "ABSENT").length,
    [roster, manualStatus]
  );

  const totalLeave = useMemo(
    () => [...roster.attendance, ...roster.remaining, ...roster.onLeave].filter((s) => manualStatus[s.id] === "LEAVE").length,
    [roster, manualStatus]
  );

  const handleLocationDialogClose = () => {
    setLocationDialogOpen(false);
    setPendingSessionCreation(null);
  };

  const handleEnableLocation = async () => {
    try {
      setLoading(true);
      const geo = await resolveGeo();
      if (!hasValidGeo(geo)) {
        setError(geo?.error || 'Location permission is required to take attendance.');
        return;
      }
      setLocationDialogOpen(false);
      if (pendingSessionCreation) {
        const { mode, classId, subjectId, timeTableSlotId, lectureDate } = pendingSessionCreation;
        if (mode === 'BOTH') {
          await proceedWithBothSessionsCreation(classId, subjectId, geo, timeTableSlotId, lectureDate);
        } else {
          await proceedWithSessionCreation(mode, classId, subjectId, geo, timeTableSlotId, lectureDate);
        }
        setPendingSessionCreation(null);
      }
    } catch (e) {
      setError('Failed to enable location services. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center" }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Smart Attendance</Typography>
          <Typography variant="body2" color="text.secondary">
            Integrated QR / OTP / Manual attendance
          </Typography>
        </Box>
        {loading && <CircularProgress size={22} />}
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton size="small" onClick={() => navPeriod(-1)} aria-label="Previous period" edge="start">
                  <ChevronLeft />
                </IconButton>
                <DatePicker
                  label={view === "monthly" ? "Month" : view === "weekly" ? "Week" : "Session Date"}
                  format={DATE_DISPLAY_FORMAT}
                  value={sessionDate ? new Date(sessionDate) : null}
                  onChange={(val) => setSessionDate(toApiDate(val))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <IconButton size="small" onClick={() => navPeriod(1)} aria-label="Next period" edge="end">
                  <ChevronRight />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>View</InputLabel>
                <Select
                  value={view}
                  label="View"
                  onChange={(e) => setView(e.target.value)}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {isAdminOrInstitute && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" disabled={isDepartmentLockedViewer}>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={selectedDepartmentId}
                    label="Department"
                    onChange={async (e) => {
                      const nextDeptId = e.target.value;
                      await runAfterEndingActiveSession(() => {
                        setSelectedDepartmentId(nextDeptId);
                        // Class list is department-scoped; clear a stale class pick.
                        setSelectedClassId("");
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Department</em>
                    </MenuItem>
                    {departments.map((d) => (
                      <MenuItem key={d.id} value={String(d.id)}>{d.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={3}>
              <FormControl
                fullWidth
                size="small"
                disabled={isAdminOrInstitute && !selectedDepartmentId}
              >
                <InputLabel>Class</InputLabel>
                <Select
                  value={selectedClassId}
                  label="Class"
                  onChange={async (e) => {
                    const nextClassId = e.target.value;
                    await runAfterEndingActiveSession(() => setSelectedClassId(nextClassId));
                  }}
                >
                  <MenuItem value="">
                    <em>All Classes</em>
                  </MenuItem>
                  {effectiveClasses.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{formatClassLabel(c)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Subject (Optional)</InputLabel>
                <Select
                  value={selectedSubjectId}
                  label="Subject (Optional)"
                  onChange={async (e) => {
                    const nextSubjectId = e.target.value;
                    await runAfterEndingActiveSession(() => setSelectedSubjectId(nextSubjectId));
                  }}
                >
                  <MenuItem value="">All Subjects</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Schedule Display */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Schedule</Typography>
          {schedulesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
                Loading schedules...
              </Typography>
            </Box>
          ) : (
            (() => {
              let filteredSchedules = schedules;
              if (selectedClassId) {
                filteredSchedules = filteredSchedules.filter((schedule) => String(schedule.classId) === String(selectedClassId));
              }
              if (selectedSubjectId) {
                filteredSchedules = filteredSchedules.filter((schedule) => String(schedule.subjectId) === String(selectedSubjectId));
              }

              return filteredSchedules.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {selectedClassId || selectedSubjectId
                    ? `No schedules found for the selected filters (${sessionDate})`
                    : `No schedules found for ${sessionDate}`
                  }
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {filteredSchedules.map((schedule, index) => {
                    const attendanceDone = isScheduleSlotFinalized(schedule);
                    const inSlotWindow = isInSlotTimeWindow(schedule);
                    const hasMarked = Number(schedule?.attendanceSummary?.totalMarked) > 0;
                    const canRetakeInSlot = inSlotWindow && hasMarked && !attendanceDone && canTakeAttendance;
                    const canTakeInSlot = inSlotWindow && !attendanceDone && canTakeAttendance;

                    const list = schedule.attendanceList || [];
                    const presentCount = list.length
                      ? list.filter((r) => String(r.status || "").toUpperCase() === "PRESENT").length
                      : Number(schedule?.attendanceSummary?.presentCount || 0);
                    const absentCount = list.length
                      ? list.filter((r) => {
                          const s = String(r.status || "").toUpperCase();
                          return s === "ABSENT" || s === "NOT_MARKED";
                        }).length
                      : (Number(schedule?.attendanceSummary?.absentCount || 0) +
                         Number(schedule?.attendanceSummary?.totalNotMarked || 0));
                    const totalCount = list.length
                      ? list.length
                      : (schedule?.attendanceSummary?.totalStudents != null
                          ? Number(schedule.attendanceSummary.totalStudents)
                          : (presentCount + absentCount));
                    const showAttendanceCounts =
                      (attendanceDone ||
                       hasMarked ||
                       ["COMPLETED", "ATTENDANCE_MARKED_MANUAL", "RUNNING"].includes(resolveScheduleDisplayStatus(schedule))) &&
                      totalCount > 0;

                    return (
                    <Grid item xs={12} md={6} lg={4} key={`${schedule.date}-${schedule.timeTableSlotId ?? schedule.id}-${schedule.classId}-${schedule.subjectId}-${index}`}>
                      <Card variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexGrow: 1 }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {schedule.className || `Class ${schedule.classId}`}
                              {schedule.classSection && ` - ${schedule.classSection}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {schedule.subjectName || `Subject ${schedule.subjectId}`}
                            </Typography>
                            {schedule.teacherName ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                Teacher: {schedule.teacherName}
                              </Typography>
                            ) : null}
                            <Typography variant="body2" color="primary" fontWeight={500}>
                              {schedule.dayName || new Date(schedule.date || sessionDate).toLocaleDateString("en-US", { weekday: "long" })}, {formatScheduleDate(schedule)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatTimeRange12h(schedule.startTime, schedule.endTime)}
                            </Typography>
                            <Chip
                              label={formatLectureTypeLabel(schedule)}
                              size="small"
                              variant="outlined"
                              color={
                                formatLectureTypeLabel(schedule) === "Online"
                                  ? "info"
                                  : ["Lab", "Practical"].includes(formatLectureTypeLabel(schedule))
                                    ? "secondary"
                                    : "default"
                              }
                              sx={{ mt: 0.75 }}
                            />
                            {schedule.practicalBatchCode && (
                              <Chip label={`Batch: ${schedule.practicalBatchCode}`} size="small" variant="outlined" color="primary" sx={{ mt: 0.75, ml: 0.5 }} />
                            )}
                            {schedule.isEdited && (
                              <Chip label="Edited" size="small" color="secondary" sx={{ mt: 0.75, ml: 0.5 }} />
                            )}
                            {schedule.isConverted && (
                              <Chip label="Converted" size="small" color="secondary" variant="outlined" sx={{ mt: 0.75, ml: 0.5 }} />
                            )}
                          </Box>
                          <Chip
                            label={formatScheduleStatus(resolveScheduleDisplayStatus(schedule))}
                            color={
                              (() => {
                                const s = resolveScheduleDisplayStatus(schedule);
                                if (s === "COMPLETED" || s === "ATTENDANCE_MARKED_MANUAL") return "success";
                                if (s === "NOT_TAKEN" || s === "ATTENDANCE_NOT_MARKED") return "warning";
                                if (s === "RUNNING") return "info";
                                return "default";
                              })()
                            }
                            size="small"
                          />
                        </Box>
                        {showAttendanceCounts && (
                          <Box sx={{ display: 'flex', gap: 1, my: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip label={`Total: ${totalCount}`} size="small" variant="outlined" />
                            <Chip label={`Present: ${presentCount}`} size="small" color="success" variant="outlined" />
                            <Chip label={`Absent: ${absentCount}`} size="small" color="error" variant="outlined" />
                            {resolveScheduleDisplayStatus(schedule) === "RUNNING" && (
                              <Tooltip title="View live attendance">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => setViewRosterSchedule(schedule)}
                                  aria-label="View live attendance"
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                        {!showAttendanceCounts && resolveScheduleDisplayStatus(schedule) === "RUNNING" && (
                          <Box sx={{ display: 'flex', gap: 1, my: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Tooltip title="View live attendance">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => setViewRosterSchedule(schedule)}
                                aria-label="View live attendance"
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                        <Box sx={{ mt: 'auto' }}>
                          {attendanceDone && !canRetakeInSlot && (
                            <>
                              <Button variant="outlined" startIcon={<Visibility />} fullWidth onClick={() => setViewAttendanceSchedule(schedule)}>
                                View attendance
                              </Button>
                              <Button sx={{ mt: 1 }} variant="contained" startIcon={<Download />} fullWidth onClick={() => downloadScheduleAttendanceCsv(schedule)}>
                                Download CSV
                              </Button>
                            </>
                          )}
                          {canRetakeInSlot && (
                            <>
                              <Button
                                variant="contained"
                                startIcon={<CheckCircle />}
                                onClick={() => startAttendanceForSchedule(schedule)}
                                disabled={loading || !hasAnyPermission(["attendance.start", "attendance.mark", "attendance.mark_manual"])}
                                fullWidth
                              >
                                Retake attendance
                              </Button>
                              <Button sx={{ mt: 1 }} variant="outlined" startIcon={<Visibility />} fullWidth onClick={() => setViewAttendanceSchedule(schedule)}>
                                View attendance
                              </Button>
                            </>
                          )}
                          {canTakeInSlot && !hasMarked && (
                            <Button
                              variant="contained"
                              startIcon={<CheckCircle />}
                              onClick={() => startAttendanceForSchedule(schedule)}
                              disabled={loading || !hasAnyPermission(["attendance.start", "attendance.mark", "attendance.mark_manual"])}
                              fullWidth
                            >
                              Take Attendance
                            </Button>
                          )}
                          {canTakeInSlot && canManualFromSchedule && (
                            <Button
                              sx={{ mt: 1 }}
                              variant="outlined"
                              onClick={() =>
                                runAfterEndingActiveSession(() =>
                                  navigate(`${normalizedRolePath}/smart-attendance/manual`, { state: { schedule } })
                                )
                              }
                              disabled={loading}
                              fullWidth
                            >
                              Mark Manual Attendance
                            </Button>
                          )}
                          {!canTakeInSlot && !canRetakeInSlot && canTakeAttendance && (
                            <Button
                              variant="outlined"
                              color="error"
                              disabled
                              fullWidth
                            >
                              {!isScheduleToday(schedule)
                                ? `Available on ${formatScheduleDate(schedule)}`
                                : isScheduleTimePassed(schedule)
                                  ? 'Class Time Passed'
                                  : isScheduleUpcoming(schedule) ? 'Class Not Started' : 'Not Available'}
                            </Button>
                          )}
                        </Box>
                      </Card>
                    </Grid>
                    );
                  })}
                </Grid>
              );
            })()
          )}
        </CardContent>
      </Card>

      {/* Special Sessions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Special Sessions
          </Typography>
          {specialSessionsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ ml: 2, alignSelf: "center" }}>
                Loading special sessions...
              </Typography>
            </Box>
          ) : specialSessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No special sessions found for {sessionDate}
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {specialSessions.map((s) => (
                <Grid item xs={12} md={6} lg={4} key={`special-${s.id}`}>
                  <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap title={s.title}>
                          {s.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap title={s.sessionType}>
                          {s.sessionType}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTimeIST(s.startDateTime) || "-"} → {formatDateTimeIST(s.endDateTime) || "-"}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={
                          s.myAttendanceMarked
                            ? "Attendance Marked"
                            : formatLectureScheduleStatus(s.status)
                        }
                        color={
                          s.myAttendanceMarked || String(s.status || "").toUpperCase() === "ATTENDANCE_MARKED"
                            ? "success"
                            : String(s.status || "").toUpperCase() === "COMPLETED"
                            ? "success"
                            : String(s.status || "").toUpperCase() === "RUNNING"
                              ? "info"
                              : "default"
                        }
                      />
                    </Box>

                    <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {s.qrEnabled ? <Chip size="small" label="QR" /> : null}
                      {s.otpEnabled ? <Chip size="small" label="OTP" /> : null}
                      {s.manualEnabled ? <Chip size="small" label="Manual" /> : null}
                      {s.allowExternalParticipants ? <Chip size="small" label="External" /> : null}
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() =>
                          runAfterEndingActiveSession(() =>
                            navigate(`/${normalizedRole}/special-sessions/${s.id}`)
                          )
                        }
                      >
                        View Details
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Commented out manual attendance creation cards - now using schedule-based attendance */}
      {/* {!activeSession && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    bgcolor: 'primary.light',
                    mb: 2
                  }}
                >
                  <QrCode2 sx={{ fontSize: 48, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  QR Code Attendance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Generate a QR code for students to scan and mark their attendance automatically
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<QrCode2 />}
                  onClick={() => createSession("QR")}
                  disabled={loading || !selectedClassId}
                  fullWidth
                >
                  Generate QR Session
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    bgcolor: 'secondary.light',
                    mb: 2
                  }}
                >
                  <Pin sx={{ fontSize: 48, color: 'secondary.main' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  OTP Attendance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Share a secure OTP code that students can enter to mark their attendance quickly
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Pin />}
                  onClick={() => createSession("OTP")}
                  disabled={loading || !selectedClassId}
                  fullWidth
                >
                  Generate OTP Session
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    bgcolor: 'info.light',
                    mb: 2
                  }}
                >
                  <CheckCircle sx={{ fontSize: 48, color: 'info.main' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Manual Attendance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Manually mark attendance for each student with a detailed class roster
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<CheckCircle />}
                  onClick={() => createSession("MANUAL")}
                  disabled={loading || !selectedClassId}
                  fullWidth
                >
                  Start Manual Session
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )} */}

      {activeSession ? (
        <Dialog open={dialogOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; }} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">
                {activeMode === "BOTH" ? "QR & OTP Attendance Session" : `Active ${activeMode} Session`}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={endActiveSession}
                disabled={loading}
                size="small"
              >
                End & Back to Setup
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              <Chip color="info" label={`Mode: ${activeMode === "BOTH" ? "QR & OTP" : activeMode}`} />
              <Chip label={`Session ID: ${activeSession.sessionId || "-"}`} />
              <Chip
                color="success"
                label={`Marked: ${roster.attendance.length}/${roster.attendance.length + roster.remaining.length + roster.onLeave.length || "--"}`}
              />
              {timeLeft > 0 && (
                <Chip
                  color={timeLeft < 60 ? "error" : timeLeft < 300 ? "warning" : "success"}
                  label={`Session ends in ${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`}
                />
              )}
              {tokenTimeLeft > 0 && (
                <Chip
                  color={tokenTimeLeft <= 10 ? "warning" : "default"}
                  label={`Codes refresh in ${tokenTimeLeft}s`}
                />
              )}
              <Chip color="success" label={`Present: ${roster.attendance.length}`} />
              <Chip color="warning" label={`Remaining: ${roster.remaining.length}`} />
              <Chip label={`On leave: ${roster.onLeave.length}`} />
              <Button
                variant="outlined"
                size="small"
                onClick={regenerateActive}
                disabled={loading || rotationLoading}
                sx={{ ml: 1 }}
              >
                {rotationLoading ? "Refreshing…" : "Regenerate QR & OTP"}
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            {activeSession.publicId ? (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6">QR Code for Attendance</Typography>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setFullScreenQR(true)}
                    title="Full Screen QR"
                  >
                    <Fullscreen />
                  </IconButton>
                </Box>
                <Box sx={{
                  display: 'inline-block',
                  p: 3,
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'scale(1.02)'
                  }
                }}>
                  <QRCode
                    value={buildQrCodeValue(activeSession)}
                    size={300}
                    level="H"
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  />
                </Box>
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Scan this QR code with the mobile app
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
                 
                  <Button
                    size="small"
                    startIcon={<Fullscreen />}
                    variant="contained"
                    onClick={() => setFullScreenQR(true)}
                  >
                    Full Screen
                  </Button>
                </Box>
              </Box>
            ) : null}

            {activeSession.otp ? (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>OTP Code for Attendance</Typography>
                <Box sx={{
                  display: 'inline-block',
                  p: 3,
                  bgcolor: 'grey.100',
                  borderRadius: 2,
                  boxShadow: 2,
                  minWidth: 200
                }}>
                  <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 'bold', letterSpacing: 2 }}>
                    {activeSession.otp}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  Share this OTP code with students
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={() => navigator.clipboard.writeText(String(activeSession.otp))}
                  >
                    Copy OTP: {activeSession.otp}
                  </Button>
                </Box>
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={endActiveSession}
              color="error"
              variant="contained"
              disabled={loading || !activeSession?.sessionId}
            >
              End Session
            </Button>
            <Button onClick={endActiveSession} disabled={loading}>End & Close</Button>
          </DialogActions>
        </Dialog>
      ) : null}

      {/* Location Permission Dialog */}
      <Dialog open={locationDialogOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; handleLocationDialogClose(); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Location Permission Required
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 2,
                borderRadius: '50%',
                bgcolor: 'warning.light',
                mb: 2
              }}
            >
              <Pin sx={{ fontSize: 48, color: 'warning.main' }} />
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Location services are required to take attendance. This helps ensure attendance is taken from the correct location.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please enable location permissions in your browser settings and click "Enable Location" to continue.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLocationDialogClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleEnableLocation} variant="contained" color="primary">
            Enable Location
          </Button>
        </DialogActions>
      </Dialog>

      {(activeMode === "MANUAL") ? (
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">Class Roster</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip label={`Present: ${totalPresent}`} color="success" size="small" />
                <Chip label={`Absent: ${totalAbsent}`} color="error" size="small" />
                <Chip label={`Leave: ${totalLeave}`} color="warning" size="small" />
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Roll No.</TableCell>
                    <TableCell>Student Login ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...roster.attendance, ...roster.remaining, ...roster.onLeave].map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.rollNumber || "-"}</TableCell>
                      <TableCell>{s.studentLoginID || "-"}</TableCell>
                      <TableCell>{s.fullName || s.user?.fullName || "-"}</TableCell>
                      <TableCell sx={{ width: 220 }}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={manualStatus[s.id] ?? ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setManualStatus((prev) => ({ ...prev, [s.id]: val }));
                            }}
                            disabled={activeMode !== "MANUAL"}
                          >
                            <MenuItem value="PRESENT">Present</MenuItem>
                            <MenuItem value="ABSENT">Absent</MenuItem>
                            <MenuItem value="LEAVE">Leave</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {activeMode === "MANUAL" ? (
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={submitManual} disabled={loading || !activeSession?.sessionId}>
                  Submit Manual Attendance
                </Button>
              </Box>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={Boolean(viewAttendanceSchedule)} onClose={() => setViewAttendanceSchedule(null)} maxWidth="md" fullWidth>
        <DialogTitle>Lecture attendance</DialogTitle>
        <DialogContent dividers>
          {viewAttendanceSchedule && (
            (() => {
              const list = viewAttendanceSchedule.attendanceList || [];
              const presentCount = list.filter((r) => String(r.status || "").toUpperCase() === "PRESENT").length;
              const absentCount = list.filter((r) => {
                const s = String(r.status || "").toUpperCase();
                return s === "ABSENT" || s === "NOT_MARKED";
              }).length;
              const totalCount = list.length;
              return (
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {viewAttendanceSchedule.classDisplayName || viewAttendanceSchedule.className} · {viewAttendanceSchedule.subjectName} · {viewAttendanceSchedule.date}{" "}
                    ({formatTimeRange12h(viewAttendanceSchedule.startTime, viewAttendanceSchedule.endTime)})
                  </Typography>
                  {viewAttendanceSchedule.lectureTopic ? (
                    <Typography variant="body2" sx={{ mb: 1 }}><strong>Topic:</strong> {viewAttendanceSchedule.lectureTopic}</Typography>
                  ) : null}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Chip label={`Total: ${totalCount}`} size="small" variant="outlined" />
                    <Chip label={`Present: ${presentCount}`} size="small" color="success" variant="outlined" />
                    <Chip label={`Absent: ${absentCount}`} size="small" color="error" variant="outlined" />
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Student</TableCell>
                          <TableCell>Roll No.</TableCell>
                          <TableCell>Login ID</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Marked</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {list.map((row) => (
                          <TableRow key={row.studentId}>
                            <TableCell>{row.studentName || "—"}</TableCell>
                            <TableCell>{row.rollNumber || "—"}</TableCell>
                            <TableCell>{row.studentLoginID || "—"}</TableCell>
                            <TableCell>{row.status || "—"}</TableCell>
                            <TableCell>{formatAttendanceMethodDisplay(row) || "—"}</TableCell>
                            <TableCell>{row.markedAt ? new Date(row.markedAt).toLocaleString() : "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              );
            })()
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewAttendanceSchedule(null)}>Close</Button>
          {viewAttendanceSchedule ? (
            <Button variant="contained" startIcon={<Download />} onClick={() => { downloadScheduleAttendanceCsv(viewAttendanceSchedule); }}>
              Download CSV
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      {/* READ-ONLY live roster for a RUNNING list card (present/absent/on-leave + live
          counts, auto-refreshing via 5s poll + attendance_marked socket). */}
      <LiveRosterDialog
        open={Boolean(viewRosterSchedule)}
        schedule={viewRosterSchedule}
        onClose={() => setViewRosterSchedule(null)}
      />

      {/* Full Screen QR Dialog */}
      <Dialog
        fullScreen
        open={fullScreenQR}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setFullScreenQR(false); }}
        PaperProps={{
          ref: fullScreenContainerRef,
          sx: {
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2, md: 4 },
          }
        }}
      >
        <IconButton
          edge="start"
          color="inherit"
          onClick={() => setFullScreenQR(false)}
          aria-label="close"
          sx={{ position: 'absolute', right: 24, top: 24, bgcolor: 'rgba(0,0,0,0.05)', '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' } }}
        >
          <Close />
        </IconButton>

        <Box sx={{ textAlign: 'center', maxWidth: '96vw', width: '100%' }}>
          <Typography variant="h3" fontWeight={700} gutterBottom sx={{ mb: { xs: 2, md: 3 } }}>
            Scan for Attendance
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 3, md: 6 },
              mb: 3,
            }}
          >
            {/* QR — sized to fill the available space. When an OTP is also shown
                the QR takes ~60% of the shorter viewport dimension so both fit;
                otherwise it grows as large as possible. */}
            <Box sx={{
              display: 'inline-block',
              p: { xs: 2, md: 4 },
              bgcolor: 'white',
              borderRadius: 4,
              boxShadow: 10,
            }}>
              <QRCode
                value={buildQrCodeValue(activeSession)}
                size={(() => {
                  const shortest = Math.min(viewport.width, viewport.height);
                  // Leave headroom for the title, timer, OTP panel and button.
                  const scale = activeSession?.otp ? 0.62 : 0.78;
                  return Math.max(200, Math.floor(shortest * scale));
                })()}
                level="H"
                style={{ height: "auto", maxWidth: "100%", width: "100%", display: 'block' }}
              />
            </Box>

            {/* OTP — shown when the active session issued one (BOTH / OTP mode) */}
            {activeSession?.otp ? (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                  Or enter this OTP
                </Typography>
                <Box
                  sx={{
                    display: 'inline-block',
                    px: { xs: 3, md: 5 },
                    py: { xs: 2, md: 3 },
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: 4,
                    boxShadow: 8,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      letterSpacing: { xs: 4, md: 8 },
                      fontSize: { xs: '3rem', md: '5rem' },
                      lineHeight: 1,
                    }}
                  >
                    {activeSession.otp}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                  Share this code with students
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
            Session Active: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')} remaining
          </Typography>

          <Button
            variant="contained"
            size="large"
            startIcon={<FullscreenExit />}
            onClick={() => setFullScreenQR(false)}
            sx={{ px: 4, py: 1.5, borderRadius: 2 }}
          >
            Exit Full Screen
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AttendanceWithQR;
